import React, { useCallback, useContext, useEffect, useState } from 'react'
import { generatePath } from 'react-router-dom'
import { ClientDto } from 'api/Clients/Types/Client'
import { TradingAccountBalance, TradingAccountDto } from 'api/Clients/Types/TradingAccount'
import { RestrictionDto } from 'api/Wallet/Types/RestrictionDto'
import { RestrictionTypes } from 'api/Wallet/Types/RestrictionTypes'
import { TradingAccountToWalletTransfer } from 'api/Wallet/Types/TradingAccountToWalletTransfer'
import {
  CurrencyType,
  DragonPhoenixCurrencyType,
  PHPCurrencyType,
  WalletDto,
  WalletType,
} from 'api/Wallet/Types/Wallet'
import { ToastContext } from 'components/Toast/Toast'
import { getIdFromLocationHeader } from 'utils/api'
import { getErrorResponse, ResponseError, useApiClient } from 'utils/api/ApiClient'
import { TradingAccountsApiClient } from 'utils/api/tradingAccountsApi'
import { WalletsApiClient } from 'utils/api/walletsApiClient'

import { ErrorResponse } from '../../../../api/ErrorResponse'
import { formatBooleanValue } from '../../../../utils/string.utils'
import TradingAccountToWalletETDTransferForm from './TradingAccountToWalletETDTransferForm'

type TradingAccountToWalletTransferProps = {
  client: ClientDto | null
  predefinedTradingAccount?: TradingAccountDto
  preSelectedWallet?: WalletDto

  close?(): void
  setClient?(): void
}

const useTradingAccountToWalletETDTransferFetch = (props: TradingAccountToWalletTransferProps) => {
  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)
  const walletsApiClient = useApiClient(WalletsApiClient)

  const [isLoading, setLoading] = useState<boolean>(false)
  const [currency, setCurrency] = useState<
    null | CurrencyType | DragonPhoenixCurrencyType | PHPCurrencyType | string
  >(null)

  const [tradingAccounts, setTradingAccounts] = useState<TradingAccountDto[]>([])
  const [selectedWallet, setSelectedWallet] = useState<WalletDto>()
  const [selectedTradingAccount, setSelectedTradingAccount] = useState<TradingAccountDto>()
  const [selectedBalance, setSelectedBalance] = useState<TradingAccountBalance>()

  const { predefinedTradingAccount, client } = props
  const clientId = client?.id

  const fetchTradingAccounts = useCallback(async () => {
    if (clientId) {
      const result = await tradingAccountsApiClient.getTradingAccountByClientId(clientId, {
        pageIndex: 1,
        pageSize: 100,
      })
      const etdResults = result?.items?.filter((x) => x.platformType.id === 3)
      setTradingAccounts(etdResults || [])
    }
  }, [clientId]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (clientId) {
      const fetchData = async () => {
        try {
          setLoading(true)
          await fetchTradingAccounts()
          return setLoading(false)
        } catch (error) {
          console.error(error)
          return setLoading(false)
        }
      }
      fetchData()
    }
  }, [clientId, fetchTradingAccounts])

  useEffect(() => {
    if (predefinedTradingAccount) {
      const matchTradingAccount = selectTradingAccountById(
        tradingAccounts,
        predefinedTradingAccount.id
      )
      setSelectedTradingAccount(matchTradingAccount)
    }
  }, [predefinedTradingAccount, tradingAccounts]) // eslint-disable-line react-hooks/exhaustive-deps

  const onSelectWalletForTradingAccount = async (
    tradingAccountId: string,
    taBalance: TradingAccountBalance
  ) => {
    if (!tradingAccounts || !clientId) {
      return null
    }
    const matchTradingAccount = selectTradingAccountById(tradingAccounts, tradingAccountId)
    if (matchTradingAccount) {
      try {
        setLoading(true)
        const wallets = await walletsApiClient.getWalletsByClientId(clientId)
        setSelectedTradingAccount(matchTradingAccount)
        const walletByCurrency = wallets
          .filter((x) => x.walletType.id === WalletType.ETD)
          .find((w) => w.currency.id === taBalance.currency)
        setSelectedBalance(taBalance)
        if (walletByCurrency) {
          setSelectedWallet(walletByCurrency)
          setCurrency(walletByCurrency.currency.id)
        } else {
          setSelectedWallet(undefined)
          setCurrency(null)
        }
        setLoading(false)
      } catch (error) {
        console.error('Cannot get wallet', error)
        setLoading(false)
      }
    }
  }

  const selectTradingAccountById = (
    tradingAccountItems: TradingAccountDto[],
    tradingAccountId: string
  ): TradingAccountDto | undefined => {
    return tradingAccountItems.find((x) => x.id === tradingAccountId)
  }

  return {
    onSelectWalletForTradingAccount,
    fetchTradingAccounts,
    setSelectedWallet,
    setSelectedTradingAccount,
    isLoading,
    tradingAccounts,
    currency,
    selectedWallet,
    selectedTradingAccount,
    selectedBalance,
  }
}

const useTradingAccountToWalletETDTransferMutate = (
  props: TradingAccountToWalletTransferProps,
  tradingAccountsFetchProps: ReturnType<typeof useTradingAccountToWalletETDTransferFetch>
) => {
  const { fetchTradingAccounts, setSelectedTradingAccount, setSelectedWallet } =
    tradingAccountsFetchProps

  const walletsApiClient = useApiClient(WalletsApiClient)
  const setToast = useContext(ToastContext)

  const [overrideWarningError, setOverrideWarningError] = useState<ErrorResponse>()

  const handleSubmit = async (values: TradingAccountToWalletTransfer) => {
    try {
      return await createTradingAccountToWalletTransfer({
        ...values,
        overrideFreeMarginCheck: !!overrideWarningError,
      })
    } catch (error: unknown) {
      if (canOverrideFreeMarginCheck(error)) {
        const errorResponse = getErrorResponse(error)
        errorResponse && setOverrideWarningError(errorResponse)
      }
      throw error
    }
  }

  const createTradingAccountToWalletTransfer = async (values: TradingAccountToWalletTransfer) => {
    const response = await walletsApiClient.createTradingAccountToWalletTransfer(values)
    if (response) {
      setToast({
        type: 'success',
        requireInteraction: true,
        title: 'Transaction has been submitted.',
        subtitle: 'Click for details',
        url: generatePath('/transactions/:id', {
          id: getIdFromLocationHeader(response.headers.location),
        }),
      })
    }
    await fetchTradingAccounts()

    if (props.close) {
      props.close()
    }

    return response
  }

  const canOverrideFreeMarginCheck = (error: unknown): error is ResponseError => {
    const properties = getErrorResponse(error)?.properties || {}
    const [, canOverrideFreeMarginCheckPropertyValue] =
      Object.entries(properties).find(([key]) => key === 'CanOverrideFreeMarginCheck') || []
    return formatBooleanValue(canOverrideFreeMarginCheckPropertyValue)
  }

  const resetAll = () => {
    setSelectedWallet(undefined)
    setSelectedTradingAccount(undefined)
    setOverrideWarningError(undefined)
  }

  return {
    handleSubmit,
    resetAll,
    overrideWarningError,
  }
}

const useFormatter = (props: {
  selectedWallet?: WalletDto
  selectedTradingAccount?: TradingAccountDto
}) => {
  const fromRestrictions =
    props?.selectedTradingAccount?.restrictions.filter(filterFromRestrictions) || []
  const toRestrictions = props?.selectedWallet?.restrictions.filter(filterToRestrictions) || []
  return {
    tradingAccountTooltipTitle: fromRestrictions.map((restriction) => restriction.name).join(', '),
    hasTradingAccountRestrictions: fromRestrictions?.length > 0,
    walletTooltipTitle: toRestrictions?.map((restriction) => restriction.name).join(', '),
    hasWalletRestrictions: toRestrictions?.length > 0,
  }
}

const filterFromRestrictions = (restriction: RestrictionDto) =>
  ![RestrictionTypes.TransferTo].includes(restriction.id)
const filterToRestrictions = (restriction: RestrictionDto) =>
  ![RestrictionTypes.TransferFrom].includes(restriction.id)

const TradingAccountToWalletETDTransferPage: React.FC<TradingAccountToWalletTransferProps> = (
  props
) => {
  const tradingAccountsFetchProps = useTradingAccountToWalletETDTransferFetch(props)
  const { selectedWallet, selectedTradingAccount, selectedBalance } = tradingAccountsFetchProps
  const { tradingAccounts, currency, isLoading } = tradingAccountsFetchProps
  const { onSelectWalletForTradingAccount } = tradingAccountsFetchProps

  const toWalletETDTransferMutate = useTradingAccountToWalletETDTransferMutate(
    props,
    tradingAccountsFetchProps
  )
  const { resetAll, handleSubmit, overrideWarningError } = toWalletETDTransferMutate

  const formatter = useFormatter({
    selectedWallet,
    selectedTradingAccount,
  })

  return (
    <TradingAccountToWalletETDTransferForm
      {...props}
      currency={currency}
      tradingAccounts={tradingAccounts}
      selectedBalance={selectedBalance}
      selectedWallet={selectedWallet}
      selectedTradingAccount={selectedTradingAccount}
      tradingAccountTooltipTitle={formatter.tradingAccountTooltipTitle}
      walletTooltipTitle={formatter.walletTooltipTitle}
      hasWalletRestrictions={formatter.hasWalletRestrictions}
      hasTradingAccountRestrictions={formatter.hasTradingAccountRestrictions}
      isLoading={isLoading}
      overrideWarningError={overrideWarningError}
      onSelectWalletForTradingAccount={onSelectWalletForTradingAccount}
      onSubmit={handleSubmit}
      resetAll={resetAll}
    />
  )
}

export default TradingAccountToWalletETDTransferPage
